import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Calculator, CheckCircle2 } from "lucide-react";
import { Reveal, SectionLabel, PrimaryCTA, ExpertCTA } from "@/components/ui-kit";
import { ArticleCard } from "@/pages/LearnPage";
import { ARTICLES, getArticle } from "@/content/articles";
import { guideForArticle } from "@/content/guides";
import { GuideCard } from "@/components/GuideUnlock";
import { SERVICES } from "@/data";
import { CALC_META, calcPath } from "@/components/calculators/meta";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function ArticlePage() {
  const { slug } = useParams();
  const a = getArticle(slug);

  usePageMeta({
    title: a ? a.title : "Article",
    description: a ? a.excerpt : "",
    path: `/learn/${slug}`,
  });

  if (!a) return <Navigate to="/learn" replace />;
  const service = SERVICES.find((s) => s.id === a.service);
  const calc = CALC_META[a.calculator];
  const related = ARTICLES.filter((x) => x.slug !== slug && (x.category === a.category || x.service === a.service)).slice(0, 3);
  const more = related.length ? related : ARTICLES.filter((x) => x.slug !== slug).slice(0, 3);

  return (
    <>
      <article>
        <header className="relative overflow-hidden bg-lightbg pt-28 sm:pt-32 lg:pt-36">
          <div className="pointer-events-none absolute -right-40 -top-24 h-[480px] w-[480px] rounded-full bg-royal/[0.07] blur-3xl" />
          <div className="relative mx-auto max-w-4xl px-5 pb-12 sm:px-8">
            <Link to="/learn" data-testid="article-back" className="inline-flex items-center gap-2 text-sm font-semibold text-navy/60 hover:text-navy">
              <ArrowLeft className="h-4 w-4" /> Learn hub
            </Link>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-8 flex flex-wrap items-center gap-3">
              <SectionLabel testId="article-category">{a.category}</SectionLabel>
              <span className="flex items-center gap-1.5 text-xs text-ink/50"><Clock className="h-3.5 w-3.5" /> {a.readTime} read</span>
            </motion.div>
            <motion.h1
              data-testid="article-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl"
            >
              {a.title}
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }} className="mt-5 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">
              {a.excerpt}
            </motion.p>
          </div>
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="overflow-hidden rounded-[2rem] border border-navy/10 shadow-hover">
              <img src={a.cover} alt={a.alt} className="aspect-[21/9] w-full object-cover" />
            </motion.div>
          </div>
        </header>

        <div className="bg-white py-16 sm:py-20">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_320px]">
            <div className="max-w-3xl">
              <Reveal>
                <div data-testid="article-takeaways" className="rounded-2xl border border-gold/30 bg-gold/[0.07] p-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-navy/60">Key takeaways</p>
                  <ul className="mt-3 space-y-2">
                    {a.takeaways.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-[15px] text-ink/80"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" /> {t}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <div className="mt-10 space-y-10">
                {a.sections.map(([h, body], i) => (
                  <Reveal key={h} delay={0.04}>
                    <section data-testid={`article-section-${i}`}>
                      <h2 className="font-display text-2xl font-bold tracking-tight text-navy">{h}</h2>
                      <p className="mt-3 text-[17px] leading-[1.75] text-ink/75">{body}</p>
                    </section>
                  </Reveal>
                ))}
              </div>
              <p className="mt-12 text-xs leading-relaxed text-ink/45">
                This article is for general education only and is not personalised financial, tax or legal advice. Investments are subject to market risks. Please consider your own circumstances or speak to us before acting.
              </p>
            </div>

            <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start">
              <GuideCard guide={guideForArticle(a.slug)} compact />
              {calc && (
                <Link to={calcPath(a.calculator)} data-testid="article-calc-link" className="group block rounded-2xl border border-navy/[0.08] bg-lightbg/60 p-6 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-hover">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-gold"><Calculator className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-display text-lg font-bold text-navy">{calc.title}</h3>
                  <p className="mt-1 text-sm text-ink/60">{calc.line}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-royal">Run the numbers <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                </Link>
              )}
              {service && (
                <Link to={`/services/${service.id}`} data-testid="article-service-link" className="block rounded-2xl border border-navy/[0.08] bg-white p-6 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-hover">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Related service</p>
                  <h3 className="mt-2 font-display text-lg font-bold text-navy">{service.title}</h3>
                  <p className="mt-1 text-sm text-ink/60">{service.line}</p>
                </Link>
              )}
              <div className="rounded-2xl bg-navy p-6 text-white">
                <h3 className="font-display text-lg font-bold">Want this applied to your situation?</h3>
                <p className="mt-2 text-sm text-white/70">Share your goals and we'll suggest a sensible first step.</p>
                <div className="mt-5 flex flex-col gap-2">
                  <PrimaryCTA testId="article-primary-cta" className="!px-5 !py-3 !text-sm">Get my plan</PrimaryCTA>
                  <ExpertCTA dark testId="article-expert-cta" className="!px-5 !py-3 !text-sm" />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <section className="bg-lightbg py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <SectionLabel testId="article-related-label">Keep reading</SectionLabel>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {more.map((x, i) => <ArticleCard key={x.slug} a={x} i={i} />)}
          </div>
        </div>
      </section>
    </>
  );
}
